/**
 * SeriesLandingPage — public template-driven landing page for a single
 * brand / series. Sections:
 *  • Hero (image, name, tagline, "Join" CTA scrolling to plans)
 *  • About / Perks
 *  • Upcoming events grid (each card links to event detail page)
 *  • Plans pricing table → opens checkout
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Calendar, Users, CheckCircle2, Ticket, Sparkles, ArrowRight, Mail,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatEventDate(dateStr, timeStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    + (timeStr ? ` · ${timeStr}` : '');
}

export default function SeriesLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/brands/by-slug/${slug}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? 'Series not found' : 'Failed to load series');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const startCheckout = async () => {
    if (!checkoutPlan) return;
    const email = user?.email || guestEmail.trim();
    const name = user?.name || guestName.trim();
    if (!email) { toast.error('Email required'); return; }
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/memberships/checkout`, {
        plan_id: checkoutPlan.plan_id,
        origin_url: window.location.origin,
        buyer_email: email,
        buyer_name: name,
      }, { headers });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
      </div>
    );
  }
  if (error || !data?.brand) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-3">
        <h2 className="text-xl font-semibold text-foreground">{error || 'Series not found'}</h2>
        <Link to="/series" className="text-sm text-indigo-300 hover:text-indigo-200">← Back to all series</Link>
      </div>
    );
  }

  const { brand, events, plans, members_count } = data;
  const accent = brand.accent_color || '#8b5cf6';
  const upcomingEvents = events.filter((e) => !e.event_date || e.event_date >= new Date().toISOString().slice(0, 10));
  const pastEvents = events.filter((e) => e.event_date && e.event_date < new Date().toISOString().slice(0, 10));

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="series-landing">
      {/* HERO */}
      <div
        className="relative pt-20 pb-16 bg-gradient-to-br from-indigo-950/60 via-purple-950/40 to-background border-b border-border"
        style={brand.hero_image_url ? {
          backgroundImage: `linear-gradient(rgba(10,10,20,0.78), rgba(10,10,20,0.92)), url(${brand.hero_image_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : {}}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-4">
            {brand.logo_url && (
              <img src={brand.logo_url} alt="" className="h-12 w-12 rounded-lg border border-border bg-background object-cover" />
            )}
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: accent }}>
              Series · {members_count} member{members_count === 1 ? '' : 's'}
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground" data-testid="brand-name">
            {brand.name}
          </h1>
          {brand.tagline && (
            <p className="text-lg text-foreground/80 mt-3 max-w-2xl">{brand.tagline}</p>
          )}
          {plans?.length > 0 && (
            <Button
              className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white"
              size="lg"
              onClick={() => document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-join-cta"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Become a member
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 space-y-10">
        {/* ABOUT + PERKS */}
        {(brand.description || (brand.perks || []).length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-3">
              <h2 className="text-base font-semibold text-foreground uppercase tracking-wider text-sm">About</h2>
              <p className="text-foreground/80 leading-relaxed whitespace-pre-line">{brand.description}</p>
            </div>
            {brand.perks?.length > 0 && (
              <Card className="bg-card border-border">
                <CardContent className="p-5 space-y-2">
                  <h3 className="font-semibold text-foreground text-sm uppercase tracking-wider">Member perks</h3>
                  <ul className="space-y-2 mt-2">
                    {brand.perks.map((p, i) => (
                      <li key={i} className="text-sm text-foreground/80 flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* PLANS / PRICING */}
        {plans?.length > 0 && (
          <div id="plans-section" data-testid="plans-section">
            <h2 className="text-2xl font-bold text-foreground mb-4">Pick your membership</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((p) => (
                <Card key={p.plan_id} className="bg-card border-border hover:border-indigo-700/60 transition-colors" data-testid={`plan-${p.plan_id}`}>
                  <CardContent className="p-5 space-y-3">
                    <div>
                      <h3 className="font-bold text-foreground">{p.name}</h3>
                      <div className="mt-1">
                        <span className="text-3xl font-bold text-foreground">${p.price}</span>
                        {p.pricing_model === 'subscription' && (
                          <span className="text-sm text-muted-foreground">/{p.billing_interval}</span>
                        )}
                        {p.pricing_model === 'passport' && p.event_limit && (
                          <span className="text-sm text-muted-foreground"> · {p.event_limit} events</span>
                        )}
                      </div>
                    </div>
                    {p.description && (
                      <p className="text-sm text-muted-foreground line-clamp-3">{p.description}</p>
                    )}
                    <ul className="space-y-1 text-sm">
                      {p.benefits?.free_admission && (
                        <li className="flex items-start gap-2 text-foreground/80">
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                          Free admission to brand events
                        </li>
                      )}
                      {p.benefits?.ticket_discount_percent > 0 && (
                        <li className="flex items-start gap-2 text-foreground/80">
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                          {p.benefits.ticket_discount_percent}% off every ticket
                        </li>
                      )}
                      {p.benefits?.early_access_hours > 0 && (
                        <li className="flex items-start gap-2 text-foreground/80">
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                          {p.benefits.early_access_hours}h early access to new events
                        </li>
                      )}
                    </ul>
                    <Button
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => setCheckoutPlan(p)}
                      data-testid={`plan-join-${p.plan_id}`}
                    >
                      {p.pricing_model === 'subscription' ? 'Subscribe' : 'Buy passport'}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* UPCOMING EVENTS */}
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-indigo-300" /> Upcoming events
          </h2>
          {upcomingEvents.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-6 text-center text-sm text-muted-foreground">
                No upcoming events yet — check back soon.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="series-events-grid">
              {upcomingEvents.map((ev) => (
                <Link
                  key={ev.event_id}
                  to={`/events/${(ev.city || '').toLowerCase()}/${ev.slug || ev.event_id}`}
                  data-testid={`series-event-${ev.event_id}`}
                  className="group"
                >
                  <Card className="bg-card border-border hover:border-indigo-700/60 transition-colors h-full">
                    <CardContent className="p-4 flex gap-3">
                      {ev.image_url && (
                        <img src={ev.image_url} alt="" className="w-20 h-20 rounded-md object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-amber-300/90 font-semibold uppercase tracking-wider">
                          {formatEventDate(ev.event_date, ev.event_time)}
                        </div>
                        <h3 className="font-semibold text-foreground mt-1 group-hover:text-indigo-300 transition-colors line-clamp-2">
                          {ev.name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {ev.venue_name || ev.venue_city || ''}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* PAST EVENTS (collapsed list) */}
        {pastEvents.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Past events · {pastEvents.length}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-muted-foreground">
              {pastEvents.slice(0, 12).map((ev) => (
                <div key={ev.event_id} className="truncate">
                  {formatEventDate(ev.event_date)} · {ev.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CHECKOUT MODAL */}
      {checkoutPlan && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !submitting && setCheckoutPlan(null)}
          data-testid="checkout-modal"
        >
          <Card className="bg-card border-border max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-amber-300 font-semibold">Joining {brand.name}</div>
                <h3 className="text-xl font-bold text-foreground mt-1">{checkoutPlan.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  <span className="text-foreground font-semibold">${checkoutPlan.price}</span>
                  {checkoutPlan.pricing_model === 'subscription' && ` / ${checkoutPlan.billing_interval}`}
                  {checkoutPlan.pricing_model === 'subscription' && ' · Cancel anytime — access until the period ends.'}
                </p>
              </div>
              {!user && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Your email</Label>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      data-testid="checkout-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Your name</Label>
                    <Input
                      placeholder="Full name"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      data-testid="checkout-name"
                    />
                  </div>
                </div>
              )}
              {user && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 p-2.5 rounded-md">
                  <Mail className="h-3.5 w-3.5" />
                  <span>Signed in as <span className="text-foreground font-medium">{user.email}</span></span>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" onClick={() => setCheckoutPlan(null)} disabled={submitting} data-testid="checkout-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={startCheckout}
                  disabled={submitting}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="checkout-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ticket className="h-4 w-4 mr-2" />}
                  Continue to payment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
